import { PLAN_TYPES, type Plan } from '@common/credits/ProfessionalEdition/types';
import { isFreePlan } from '@common/credits/ProfessionalEdition/utils';
import { formatCost } from '@prophecy/utils/fmt/number';
import { format } from 'date-fns';

export const formatPlanPrice = (price: number | null) => {
  if (price === null) {
    return 'Custom';
  }

  return `$${formatCost(price)}`;
};

export const formatPlanUnit = (price: number | null, unit: string | undefined) => {
  if (price === null) {
    return undefined;
  }

  if (unit === 'per user per month') {
    return '/ user / month';
  }

  if (unit === 'per year') {
    return `/ year`;
  }
  return unit;
};

export const computePlanSubtitle = (
  plan: Pick<Plan, 'name' | 'maxUsers' | 'contractDuration' | 'contractDurationUnit'>
): string => {
  switch (plan.name) {
    case PLAN_TYPES.Starter:
      return 'Free forever';
    case PLAN_TYPES.FreeTrial:
      return plan.contractDuration && plan.contractDurationUnit
        ? `${plan.contractDuration}-${plan.contractDurationUnit} free trial`
        : 'Free trial';
    case PLAN_TYPES.Pro:
      return `Billed monthly`;
    case PLAN_TYPES.Express:
      return plan.maxUsers ? `Up to ${plan.maxUsers} users · Billed annually` : 'Billed annually';
    case PLAN_TYPES.Enterprise:
      return 'Unlimited users · Billed annually';
    default:
      return '';
  }
};

export const computePlanRenewalInfo = (plan: Pick<Plan, 'name'>, billingPeriodEnd?: string): string => {
  // Free plans don't renew, so show the subtitle (e.g. 'Free forever' / '14-day free trial') instead
  if (isFreePlan(plan.name)) {
    return computePlanSubtitle(plan);
  }

  if (!billingPeriodEnd) {
    return 'Renewal date unavailable';
  }

  try {
    const parsed = new Date(billingPeriodEnd);
    if (Number.isNaN(parsed.getTime())) {
      return 'Renewal date unavailable';
    }
    return `Renews on ${format(parsed, 'MMM d, yyyy')}`;
  } catch {
    return 'Renewal date unavailable';
  }
};

export const formatUpdatedAt = (updatedAt: string): string => {
  try {
    return format(new Date(updatedAt), "d MMM yyyy 'at' h:mm a");
  } catch {
    return 'Not available';
  }
};
